import React, { useState } from 'react';
import { Table, Typography, Space, Avatar, Switch, Tag, Input, Button } from 'antd';
import {
    DashboardOutlined,
    FileTextOutlined,
    UserOutlined,
    SearchOutlined,
    FilterOutlined,
    PlusOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useParams, useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

interface CollectionItem {
    key: string;
    type: 'dashboard' | 'report';
    name: string;
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
    schedulerEnabled: boolean;
}

const mockData: CollectionItem[] = [
    {
        key: '1',
        type: 'dashboard',
        name: 'Sales Overview Q3',
        createdBy: 'Aman Ruhela',
        createdAt: '2025-05-20',
        updatedBy: 'Aman Ruhela',
        updatedAt: '2 hours ago',
        schedulerEnabled: true,
    },
    {
        key: '2',
        type: 'report',
        name: 'Inventory Audit Log',
        createdBy: 'System Monitor',
        createdAt: '2025-05-18',
        updatedBy: 'Sarah Connor',
        updatedAt: '1 day ago',
        schedulerEnabled: false,
    },
    {
        key: '3',
        type: 'dashboard',
        name: 'Marketing Campaign Performance',
        createdBy: 'Marketing Team',
        createdAt: '2025-05-15',
        updatedBy: 'Admin',
        updatedAt: '3 days ago',
        schedulerEnabled: true,
    },
    {
        key: '4',
        type: 'report',
        name: 'User Retention Analysis',
        createdBy: 'Aman Ruhela',
        createdAt: '2025-05-10',
        updatedBy: 'Aman Ruhela',
        updatedAt: '5 days ago',
        schedulerEnabled: false,
    },
    {
        key: '5',
        type: 'dashboard',
        name: 'Executive Summary',
        createdBy: 'CEO Office',
        createdAt: '2025-05-01',
        updatedBy: 'Admin',
        updatedAt: '1 week ago',
        schedulerEnabled: true,
    },
];

const CollectionsPage: React.FC = () => {
    const { collectionId, workspaceId } = useParams<{ collectionId: string; workspaceId: string }>();
    const [searchText, setSearchText] = useState('');
    const navigate = useNavigate();

    const columns: ColumnsType<CollectionItem> = [
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 80,
            render: (type) => (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    background: type === 'dashboard' ? '#e6f7ff' : '#fff7e6',
                    color: type === 'dashboard' ? '#1890ff' : '#fa8c16',
                }}>
                    {type === 'dashboard' ? <DashboardOutlined /> : <FileTextOutlined />}
                </div>
            ),
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong>{text}</Text>,
        },
        {
            title: 'Created By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            width: 150,
            render: (text) => (
                <Space>
                    <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#fde3cf', color: '#f56a00' }} />
                    <Text type="secondary">{text}</Text>
                </Space>
            ),
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 120,
            render: (text) => <Text type="secondary">{text}</Text>,
        },
        {
            title: 'Updated By',
            dataIndex: 'updatedBy',
            key: 'updatedBy',
            width: 150,
            render: (text) => (
                <Space>
                    <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#87e8de', color: '#006d75' }} />
                    <Text type="secondary">{text}</Text>
                </Space>
            ),
        },
        {
            title: 'Updated At',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 120,
            render: (text) => <Text type="secondary">{text}</Text>,
        },
        {
            title: 'Scheduler',
            dataIndex: 'schedulerEnabled',
            key: 'schedulerEnabled',
            width: 100,
            render: (enabled) => (
                <Switch size="small" checked={enabled} />
            ),
        },
    ];

    // Mock looking up collection name based on ID
    const getCollectionName = (id: string | undefined) => {
        if (!id) return 'Collection';
        if (id === '1') return 'Our analytics';
        if (id === '2') return 'Your personal collection';
        if (id === '3') return '3PL Analytics';
        return `Collection ${id}`;
    };

    return (
        <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100%' }}>
            {/* Header section similar to System Activity for consistency */}
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={3} style={{ marginBottom: 4, color: '#262626' }}>{getCollectionName(collectionId)}</Title>
                    <Text type="secondary">Manage dashboards and reports in this collection</Text>
                </div>
                <Space>
                    <Input
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        placeholder="Search items..."
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        style={{ width: 250, borderRadius: 6 }}
                    />
                    <Button icon={<FilterOutlined />}>Filter</Button>
                    <Button type="primary" icon={<PlusOutlined />}>New Item</Button>
                </Space>
            </div>

            <Table
                columns={columns}
                dataSource={mockData}
                pagination={{ pageSize: 10 }}
                rowClassName="collection-row"
                style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', background: '#fff', cursor: 'pointer' }}
                onRow={(record) => ({
                    onClick: () => {
                        navigate(`/${workspaceId}/analytics/${record.key}`);
                    },
                })}
            />
        </div>
    );
};

export default CollectionsPage;
